import { generateUrl } from '@legacy/utils/fetcher';
import { capitalize } from '@legacy/utils/string';
import { formatUrl } from '@legacy/utils/url';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { getSearch } from './searchLegacy';
import type {
  MultiSearchResult,
  Hit,
  EmployeeDocument,
  CustomerDocument,
  ContactDocument,
  InvoiceDocument,
  ProjectDocument,
} from './searchLegacy.types';
import { useGetTypeSenseAPIKeys } from '@/api/REST/typeSenseAPIKeys';
import type { ISearchItem, ISearchResult } from '@/legacy/apis/types/searchAPI';

const SEARCH_KEY = 'TYPESENSE_SEARCH';

const formatSearchResultsList = (list: ISearchItem[]) => {
  return (
    list.map(({ name, pageUrl, ...restProps }) => ({
      name: name ? capitalize(name) : '',
      pageUrl: formatUrl(pageUrl),
      ...restProps,
    })) ?? []
  );
};

const isSearchResultsListEmpty = (collection: ISearchItem[]) => {
  return collection.length <= 0;
};

const getActionUrl = (collectionName: string, id: number) => {
  switch (collectionName) {
    case 'customers': {
      return generateUrl({
        path: '/CRM/Customer/Edit/',
        key: 'typesenseactions',
        itemId: id.toString(),
      });
    }
    case 'employees': {
      return generateUrl({
        path: '/Employee/EmployeeOverview/Index/',
        key: 'typesenseactions',
        itemId: id.toString(),
      });
    }
    case 'projects': {
      return generateUrl({
        path: '/ProjectManagement/Plan/Task/',
        key: 'typesenseactions',
        itemId: id.toString(),
      });
    }
    case 'invoices': {
      return generateUrl({
        path: '/Financial/Invoicing/Edit/',
        key: 'typesenseactions',
        itemId: id.toString(),
      });
    }
    case 'contacts': {
      return generateUrl({
        path: '/CRM/Contact/Edit/',
        key: 'typesenseactions',
        itemId: id.toString(),
      });
    }
    default: {
      console.error(
        `Unknown collection name for URL generation: ${collectionName}`,
      );
      return '';
    }
  }
};

const getDocumentWithMappedHighlights = (hit: Hit) => {
  const document = { ...hit.document };

  if (hit.highlights && hit.highlights.length > 0) {
    hit.highlights.forEach((highlight) => {
      document[highlight.field] = highlight.snippet;
    });
  }
  return document;
};

const formatNameStringForUI = (
  collectionName: string,
  document: Hit['document'],
) => {
  const name = document.Name || '';

  switch (collectionName) {
    case 'employees': {
      const initials = (document as EmployeeDocument).Initials;
      return `${name}${initials ? ` (${initials})` : ''}`;
    }
    case 'customers': {
      const number = (document as CustomerDocument).Number;
      return `${name}${number ? ` (${number})` : ''}`;
    }
    case 'contacts': {
      const customerName = (document as ContactDocument).CustomerName;
      return `${name}${customerName ? ` (${customerName})` : ''}`;
    }
    case 'projects': {
      const projectDoc = document as ProjectDocument;
      const customerName = projectDoc.CustomerName;
      const number = projectDoc.Number;
      let result = name;
      if (customerName) {
        result += ` (${customerName})`;
      }
      if (number && number !== '') {
        result += ` - ${number}`;
      }
      return result;
    }
    case 'invoices': {
      const customerName = (document as InvoiceDocument).CustomerName;
      return `${name}${customerName ? ` (${customerName})` : ''}`;
    }
    default:
      return name;
  }
};

export const useSearchLegacy = (
  searchValue: string,
  searchCategories?: string[],
) => {
  const { data: apiKeys, isError: isKeyError } = useGetTypeSenseAPIKeys();

  const { data, ...restProps } = useQuery<MultiSearchResult>({
    queryKey: [SEARCH_KEY, searchValue, searchCategories ?? [], apiKeys],
    // assert apiKeys is not undefined since we added the check in the enabled condition
    queryFn: () => getSearch(searchValue, searchCategories ?? [], apiKeys!),
    placeholderData: keepPreviousData,
    staleTime: 5000,
    enabled: !!searchValue && !isKeyError && !!apiKeys,
  });

  if (isKeyError || !apiKeys) {
    console.error('Failed to fetch TypeSense API keys');

    return {
      ...restProps,
      searchResult: [],
      isSearchResultEmpty: true,
      isError: true,
    };
  }

  if (data && data.results) {
    const results = {
      contacts: [] as ISearchItem[],
      customers: [] as ISearchItem[],
      projects: [] as ISearchItem[],
      invoices: [] as ISearchItem[],
      employees: [] as ISearchItem[],
    };

    data.results.forEach((result) => {
      if ('error' in result) {
        console.error('Search error:', result.code, result.error);
        return;
      }

      const collectionName = result['request_params']?.collection_name ?? '';

      const hits = result.hits?.map((hit: Hit) => {
        const document = getDocumentWithMappedHighlights(hit);
        return {
          id: document['SourceId'],
          name: formatNameStringForUI(collectionName, document),
          pageUrl: getActionUrl(collectionName, document['SourceId']),
        };
      });

      if (collectionName in results) {
        results[collectionName] = hits;
      } else {
        console.warn(`Unexpected collection name: ${collectionName}`);
      }
    });

    const searchResult: ISearchResult[] = [
      {
        category: 'Customers',
        list: formatSearchResultsList(results.customers),
        isEmpty: isSearchResultsListEmpty(results.customers),
      },
      {
        category: 'Contacts',
        list: formatSearchResultsList(results.contacts),
        isEmpty: isSearchResultsListEmpty(results.contacts),
      },
      {
        category: 'Projects',
        list: formatSearchResultsList(results.projects),
        isEmpty: isSearchResultsListEmpty(results.projects),
      },
      {
        category: 'Invoices',
        list: formatSearchResultsList(results.invoices),
        isEmpty: isSearchResultsListEmpty(results.invoices),
      },
      {
        category: 'Employees',
        list: formatSearchResultsList(results.employees),
        isEmpty: isSearchResultsListEmpty(results.employees),
      },
    ];

    return {
      searchResult,
      isSearchResultEmpty: searchResult.every((result) => result.isEmpty),
      ...restProps,
    };
  }
  return {
    searchResult: [],
    isSearchResultEmpty: true,
    ...restProps,
  };
};
